import '@xyflow/react/dist/style.css';
import Button from '@/common/components/Button';
import { Mapper } from '@/common/services/mapper';
import { CompanyBranchData, CompanyBranchTransformerToStructureResponse } from '@/common/types/companyBranches.type';
import { Node, ReactFlow, useReactFlow } from '@xyflow/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import CustomNode from '../../components/structure/CustomNode';
import { useCompanyBranches } from '@/common/hooks/queries/useCompanyBranches';
import { getLayoutedElements } from '../../services/getLayoutElements';
import '../../styles/style.css';
import { CompanyBranchesTransformer } from '../../services/companyBranchesTransformer';
import ListHeader from '@/common/components/ListHeader';
import { GitFork } from 'lucide-react';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import { useMineCompany } from '@/features/company/hooks/useMineCompany';
import { SearchNode } from '@/features/companyBranches/components/structure/SearchNode';
import { ZoomButtons } from '@/features/companyBranches/components/structure/ZoomButtons';

const nodeTypes = {
  custom: CustomNode,
};

const Structure = () => {
  const { id } = useParams();
  const parentRef = useRef<HTMLDivElement>(null);
  const { setCenter } = useReactFlow();

  const [searchTerm, setSearchTerm] = useState('')
  const [matchedNodeIds, setMatchedNodeIds] = useState<string[]>([])
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null)

  const { data: company, isLoading: isLoadingCompany } = useMineCompany();

  const consumptionControlledBy = company?.current_contract.consumption_controlled_by

  const {
    data: { nodes, edges },
    isPending: isLoadingBranches,
    error: branchesError = {} as any,
    refetch: refetchBranches,
  } = useCompanyBranches({
    filters: { pageSize: 1000 },
    mapper: Mapper.mapWithTransformer<CompanyBranchData[], CompanyBranchTransformerToStructureResponse>({
      transformer: CompanyBranchesTransformer.structureTransformer(),
    }),
  });

  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    return getLayoutedElements(nodes, edges);
  }, [id, nodes, edges]);

  const goToMatrixBranch = () => {
    const matrixBranch = layoutedNodes?.find((node: Node) => node.data.parent_id === null);

    if (matrixBranch) {
      const x = matrixBranch.position.x;
      const y = matrixBranch.position.y;
      setCenter(x + 172, y + 160, { zoom: 1 });
    }
  }

  useEffect(() => {
    if (!isLoadingBranches && layoutedNodes?.length) {
      setTimeout(goToMatrixBranch);
    }
  }, [isLoadingBranches, layoutedNodes?.length]);

  if (isLoadingBranches || isLoadingCompany) return <div>Carregando...</div>;

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<GitFork className="w-10 h-10" strokeWidth={1.5} />}
        title="Estrutura"
      />

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={branchesError?.status}
        refetchFunction={refetchBranches}
      >
        <div data-testid="structure_panel_container" className='flex flex-col bg-white-100 rounded-md items-center p-4 gap-4'>
          <div className='relative flex justify-between w-full'>
            <p>Estrutura da empresa</p>

            <div className='flex items-center gap-x-2'>
              <SearchNode
                nodes={layoutedNodes}
                onSearchChange={setSearchTerm}
                onMatchesChange={setMatchedNodeIds}
                onActiveChange={setActiveNodeId}
              />

              <ZoomButtons />

              {company?.kind === 'private_company' && (
                <Link to='create' data-testid="create_new_branch_button">
                  <Button>Adicionar filial</Button>
                </Link>
              )}
            </div>
          </div>

          <div className='h-[calc(100vh-280px)] w-full' ref={parentRef}>
            <ReactFlow
              nodes={layoutedNodes?.map((node: Node) => ({
                ...node,
                data: { ...node.data,
                  parentRef,
                  companyKind: company?.kind,
                  consumptionControlledBy,
                  searchTerm,
                  isMatch: matchedNodeIds.includes(node.id),
                  isActiveMatch: node.id === activeNodeId,
                },
              }))}
              edges={layoutedEdges}
              nodeTypes={nodeTypes}
              fitView={true}
              fitViewOptions={{
                minZoom: 1,
              }}
              minZoom={0.2}
            />
          </div>
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default Structure;
